/**
 * Token Refresh API Client for iframe
 *
 * Base class for making API calls with automatic token refresh on 401 errors
 */

import { SessionExpiredError, tssErrorFromBody, type TssErrorBody } from '../../lib/errors'
import { iframeSession } from './iframe-session'
import { sdkVersionHeaders } from './sdk-version'

// Build-time injected variable (declared in globals.d.ts)
declare const __EMBARK_TSS_URL__: string

interface TokenRefreshResult {
  token: string | null
  /** false → transient failure (retryable); true/undefined → session is dead */
  sessionDead?: boolean
}

function parseTssErrorBody(text: string): TssErrorBody | null {
  try {
    const parsed: unknown = JSON.parse(text)
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const body = parsed as TssErrorBody
      if (typeof body.error === 'string' || typeof body.code === 'string') {
        return body
      }
    }
  } catch {
    return null
  }
  return null
}

export class TokenRefreshApiClient {
  protected tssUrl: string
  private pendingTokenRefresh: Promise<TokenRefreshResult> | null = null

  constructor() {
    this.tssUrl = (typeof __EMBARK_TSS_URL__ !== 'undefined' && __EMBARK_TSS_URL__) || 'https://api.embarkai.io/tss'
  }

  /**
   * One GET without token refresh or retry, for lookups that may fail quietly: the response body, or null on any
   * failure (not OK, network, timeout). A 401 here never rotates the session, so the caller's token stays the one
   * its next calls use.
   */
  async peek(path: string, projectId?: string, accessToken?: string, timeoutMs = 5000): Promise<any | null> {
    const url = projectId ? `${this.tssUrl}${path}${path.includes('?') ? '&' : '?'}projectId=${encodeURIComponent(projectId)}` : `${this.tssUrl}${path}`
    const headers: Record<string, string> = { ...sdkVersionHeaders() }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    try {
      const response = await fetch(url, { method: 'GET', headers, credentials: 'include', signal: typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(timeoutMs) : undefined })
      return response.ok ? await response.json() : null
    } catch {
      return null
    }
  }

  /**
   * Make API call with automatic token refresh on 401
   */
  async apiCall(
    method: string,
    path: string,
    body?: any,
    projectId?: string,
    accessToken?: string
  ): Promise<any> {
    let url = `${this.tssUrl}${path}`

    // Add projectId as query parameter
    if (projectId) {
      const separator = url.includes('?') ? '&' : '?'
      url = `${url}${separator}projectId=${encodeURIComponent(projectId)}`
    }

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...sdkVersionHeaders()
    }

    // Add Authorization header if token is provided
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'include' // Send cookies
      })

      // Handle 401 - Token expired or session gone
      if (response.status === 401 && accessToken) {
        console.log('[iframe][TokenRefresh] Got 401, inspecting error body...')

        let errorData: { error_code?: string; action?: string } | null = null
        let parseFailed = false
        try {
          errorData = await response.json()
        } catch (parseError) {
          // non-JSON 401 = not from tss-api (proxy) — try the refresh path below
          console.error('[iframe][TokenRefresh] Failed to parse 401 response, will attempt refresh:', parseError)
          parseFailed = true
        }

        // server says the session is dead — refresh is pointless
        if (errorData && (errorData.error_code === 'SESSION_NOT_FOUND' || errorData.action === 'REAUTHENTICATE')) {
          console.error('[iframe][TokenRefresh] ❌ Session expired, re-authentication required')
          throw new SessionExpiredError()
        }

        if (parseFailed || (errorData && (errorData.error_code === 'TOKEN_EXPIRED' || errorData.action === 'REFRESH_TOKEN'))) {
          // The iframe session refreshes itself; only legacy tokens come from the parent
          const ownSession = iframeSession.owns(accessToken)
          console.log(`[iframe][TokenRefresh] TOKEN_EXPIRED detected, refreshing ${ownSession ? 'the iframe session' : 'via the parent'}...`)

          const refresh = ownSession ? await iframeSession.refresh() : await this.requestNewTokenFromParent()

          if (!refresh.token) {
            // sessionDead=false → transient failure (503/network), retryable;
            // true or unknown (legacy parent) → re-login
            if (refresh.sessionDead === false) {
              console.error('[iframe][TokenRefresh] Token refresh temporarily unavailable')
              throw new Error('Token refresh temporarily unavailable, please retry')
            }
            console.error('[iframe][TokenRefresh] ❌ Token refresh failed, re-authentication required')
            throw new SessionExpiredError()
          }
          const newToken = refresh.token

          console.log('[iframe][TokenRefresh] ✅ Got new token, retrying request...')

          // Retry request with new token
          headers['Authorization'] = `Bearer ${newToken}`
          const retryResponse = await fetch(url, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
            credentials: 'include'
          })

          if (retryResponse.status === 401) {
            // still 401 with a fresh token — session is unrecoverable
            console.error('[iframe][TokenRefresh] ❌ Still 401 after token refresh, re-authentication required')
            throw new SessionExpiredError()
          }

          if (!retryResponse.ok) {
            const retryErrorText = await retryResponse.text()
            const retryErrorBody = parseTssErrorBody(retryErrorText)
            if (retryErrorBody) {
              throw tssErrorFromBody(retryResponse.status, retryErrorBody)
            }
            throw Object.assign(new Error(`API call failed after token refresh (${retryResponse.status}): ${retryErrorText}`), {
              status: retryResponse.status
            })
          }

          return await retryResponse.json()
        }

        throw new Error(`API call failed (401): Authentication required`)
      }

      if (!response.ok) {
        const errorText = await response.text()
        const errorBody = parseTssErrorBody(errorText)
        if (errorBody) {
          throw tssErrorFromBody(response.status, errorBody)
        }
        throw Object.assign(new Error(`API call failed (${response.status}): ${errorText}`), { status: response.status })
      }

      return await response.json()
    } catch (error) {
      console.error(`[iframe][TokenRefresh] API call failed: ${method} ${path}`, error)
      throw error instanceof Error ? error : new Error('Unknown API error')
    }
  }

  /**
   * Request new access token from parent window
   */
  private async requestNewTokenFromParent(): Promise<TokenRefreshResult> {
    // If there's already a pending token refresh request, wait for it
    if (this.pendingTokenRefresh) {
      console.log('[iframe][TokenRefresh] Token refresh already in progress, waiting...')
      return this.pendingTokenRefresh
    }

    // Create a new token refresh promise
    this.pendingTokenRefresh = new Promise<TokenRefreshResult>((resolve) => {
      const messageId = `token_refresh_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`
      const timeout = setTimeout(() => {
        cleanup()
        // no answer from parent — transient, not a dead session
        resolve({ token: null, sessionDead: false })
      }, 30000) // 30 second timeout

      const messageHandler = (event: MessageEvent) => {
        const message = event.data

        // Check if this is the response to our token refresh request
        // Accept both new (IFRAME_MSG_) and legacy (LUMIA_PASSPORT_) prefixes
        const type = message?.type?.startsWith?.('LUMIA_PASSPORT_')
          ? message.type.replace('LUMIA_PASSPORT_', 'IFRAME_MSG_')
          : message?.type

        if (message && type === 'IFRAME_MSG_TOKEN_REFRESHED' && message.messageId === messageId) {
          cleanup()

          if (message.accessToken) {
            console.log('[iframe][TokenRefresh] ✅ Received new token from parent')
            resolve({ token: message.accessToken })
          } else {
            console.error('[iframe][TokenRefresh] ❌ Token refresh failed:', message.error)
            // legacy parents do not send sessionDead — treat unknown as dead (old behavior)
            resolve({ token: null, sessionDead: message.sessionDead !== false })
          }
        }
      }

      const cleanup = () => {
        clearTimeout(timeout)
        window.removeEventListener('message', messageHandler)
        this.pendingTokenRefresh = null
      }

      // Listen for response
      window.addEventListener('message', messageHandler)

      // Send token refresh request to parent
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          {
            type: 'IFRAME_MSG_REQUEST_NEW_TOKEN',
            messageId,
            timestamp: Date.now()
          },
          '*' // Parent will validate origin
        )
        console.log('[iframe][TokenRefresh] 📤 Sent IFRAME_MSG_REQUEST_NEW_TOKEN to parent')
      } else {
        cleanup()
        resolve({ token: null, sessionDead: false })
      }
    })

    return this.pendingTokenRefresh
  }
}
